import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";

interface GridContainerProps {
  children: React.ReactNode;
  className?: string;
  columns?: number;
}

// MOUSE / desktop / laptop devices ALWAYS get the exact requested column count
// at every width — desktop is never touched. Only TOUCH devices (iPad / phones,
// `any-pointer: coarse`) step the count DOWN to avoid crushed cards. The width
// tiers are read ONLY inside the touch branch, so a laptop that happens to share
// an iPad pixel width (1024–1366) is never affected (it isn't a coarse pointer).
function effectiveColumns(columns: number): number {
  if (typeof window === "undefined") return columns;
  if (!window.matchMedia("(any-pointer: coarse)").matches) return columns;
  const w = window.innerWidth;
  if (w < 640) return 1;
  if (w < 1024) return Math.min(columns, 2);
  return Math.min(columns, 3);
}

function useResponsiveColumns(columns: number): number {
  const [cols, setCols] = useState(() => effectiveColumns(columns));
  useEffect(() => {
    const coarse = window.matchMedia("(any-pointer: coarse)");
    const recompute = () => setCols(effectiveColumns(columns));
    recompute();
    coarse.addEventListener("change", recompute);
    window.addEventListener("resize", recompute);
    return () => {
      coarse.removeEventListener("change", recompute);
      window.removeEventListener("resize", recompute);
    };
  }, [columns]);
  return cols;
}

export function GridContainer({
  children,
  className,
  columns = 3,
}: GridContainerProps) {
  const cols = useResponsiveColumns(columns);
  return (
    <div
      className={cn("grid gap-[20px]", className)}
      style={{
        gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      }}
    >
      {children}
    </div>
  );
}

export default GridContainer;
